import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks';
import { useSearchParams } from 'react-router-dom';
import { fetchBureauEnquiryData, fetchChartData, fetchCibilOverview } from '../../../../../../store/dashboard/creditBureau/actions';
import { ENQUIRY_LOOKBACK_YEARS, EnquiryData, VendorType, resolveBureauType } from './CreditBureauConstants';
import dayjs from 'dayjs';
import { COMBINED_REPORT, DETAILED_REPORT } from '../../CreditBureau';
import clsx from 'clsx';
import styles from './Report.module.scss';
import { DASHBOARD_DATA_TYPES } from '../../../..';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { getSubdomain } from '../../../../../../utils/utils';

const useReport = ({
    selectedReport,
    selectedDirector,
    setSelectedReport,
    selectedReportConfig,
    selectedCompany,
    getGridRowHeight,
    resetQueryParams,
    creditBureauConfig,
    consentByDirectorId = {},
}) => {
    const dispatch = useAppDispatch();
    const creditBureauData = useAppSelector((state) => state.dashboard.creditBureau);
    const chartsData = creditBureauData.charts;
    const cibilOverviewData = creditBureauData.cibilOverviewData;
    const directorsList = creditBureauData?.mainData?.listDirectorsOverview || [];
    const user = useAppSelector((state) => state.user);
    const [searchParams, setSearchParams] = useSearchParams();
    const viewModeFromUrl = searchParams.get('viewMode');
    const [dataType, setDataType] = useState(viewModeFromUrl === 'table' ? DASHBOARD_DATA_TYPES.TABLE : DASHBOARD_DATA_TYPES.CHARTS);
    const [isBureauEnquiryDrawerOpen, setIsBureauEnquiryDrawerOpen] = useState(false);
    const [enquiryCount, setEnquiryCount] = useState(0);
    const [totalEnquiryCount, setTotalEnquiryCount] = useState(0);
    const bureauType = searchParams.get('bureauType') == 'All' ? null : searchParams.get('bureauType');
    /**
     * The bureau the report on screen actually belongs to.
     *
     * `bureauType` is deliberately null on the ALL tab, because that tab is not
     * bureau-specific. But a director row on it still belongs to exactly ONE
     * bureau, and which one is carried in the `directorBureauType` param. So
     * anything that needs the report's real bureau has to use this, not
     * `bureauType` — the enquiry fetch below already did, via a local, which is
     * why the Excel Analysis CTA was the only thing that broke when arriving
     * from the ALL tab: hasWorkbook(null) is false, so it never rendered.
     */
    const resolvedBureauType = resolveBureauType(bureauType, searchParams.get('directorBureauType'));
    const directorId = searchParams.get('directorId');
    const isCombinedReport = selectedReport === COMBINED_REPORT;
    const lastConsent = directorId
        ? (consentByDirectorId as Record<string, { consenterName: string; consentedAt: string }>)[directorId] || null
        : null;

    // Remove viewMode param from URL after initial read
    useEffect(() => {
        if (viewModeFromUrl) {
            searchParams.delete('viewMode');
            setSearchParams(searchParams, { replace: true });
        }
    }, []);

    useEffect(() => {
        fetchCibilOverviewData();
        return () => resetQueryParams();
    }, []);

    /**
     * Enquiries in the last ENQUIRY_LOOKBACK_YEARS, for the banner above the report. The combined
     * view spans every director so it has no single enquiry list, and the drawer this banner links
     * to reads the same endpoint.
     *
     * Takes an isCurrent callback rather than reading a ref: switching director re-runs the effect,
     * and without the guard a slower earlier response could land afterwards and leave the previous
     * director's count on screen.
     */
    const fetchEnquiryCounts = (isCurrent: () => boolean) => {
        setEnquiryCount(0);
        setTotalEnquiryCount(0);

        const bureauName = resolvedBureauType || '';
        if (isCombinedReport || !directorId || !bureauName) return;

        dispatch(
            fetchBureauEnquiryData(
                { requestPayload: { directorId, bureauName: bureauName as VendorType } },
                {
                    onSuccess: (data: EnquiryData[]) => {
                        if (!isCurrent()) return;
                        const enquiries = data || [];
                        const cutoff = dayjs().subtract(ENQUIRY_LOOKBACK_YEARS, 'year');

                        setTotalEnquiryCount(enquiries.length);
                        setEnquiryCount(
                            enquiries.filter((enquiry) => {
                                // dayjs(undefined) is *now*, so an undated enquiry would otherwise
                                // count as the most recent one there is.
                                if (!enquiry?.dateOfEnquiry) return false;

                                const enquiredOn = dayjs(enquiry.dateOfEnquiry);
                                return enquiredOn.isValid() && enquiredOn.isAfter(cutoff);
                            }).length,
                        );
                    },
                    onError: () => {
                        if (!isCurrent()) return;
                        setEnquiryCount(0);
                        setTotalEnquiryCount(0);
                    },
                },
            ),
        );
    };

    /**
     * `directorBureauType` is read from searchParams but deliberately not a dependency: it is the
     * fallback the director was opened on and does not change while a report is open, whereas
     * searchParams changes on every filter and sort. Re-running this on those would refetch the
     * enquiry list for no reason.
     */
    useEffect(() => {
        let current = true;
        fetchEnquiryCounts(() => current);

        return () => {
            current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [directorId, bureauType, isCombinedReport]);

    const getRiskClassname = (isDpd, isFirstTwo = false) => {
        return clsx({
            [styles.MetricValue]: true,
            [styles.LowRiskBadge]: !isDpd,
            [styles.MediumRiskBadge]: false,
            [styles.HighRiskBadge]: isDpd,
            [styles.GrayPill]: isFirstTwo,
        });
    };
    function getImageSource(director, bureauType) {
        if (!director && !bureauType) return null;
        return creditBureauConfig?.tabsToDisplay?.[bureauType ?? director?.creditBureau]?.logoUrl;
    }

    const fetchCibilOverviewData = () => {
        const directorId = searchParams.get('directorId');
        dispatch(
            fetchCibilOverview({
                assesseeOrgId: selectedCompany?.assesseeOrgId,
                reportId: selectedCompany?.reportId || '',
                ...(directorId && { directorId: directorId }),
                // A director report belongs to exactly one bureau (see resolvedBureauType above) —
                // reuse it here instead of the ALL-tab's bureauType so the overview cards stay scoped.
                type: directorId ? resolvedBureauType : searchParams.get('bureauType'),
            }),
        );
    };

    const openBureauEnquiryDrawer = () => {
        mixpanelBureauEnquiryDrawerClick();
        searchParams.set('openBureauEnquiryDrawer', 'true');
        searchParams.set('directorIdEnquiryDrawer', selectedDirector?.directorId);
        {
            selectedDirector?.creditBureau && searchParams.set('vendorTypeEnquiryDrawer', selectedDirector?.creditBureau);
        }
        setSearchParams(searchParams);
        setIsBureauEnquiryDrawerOpen(true);
    };

    const closeBureauEnquiryDrawer = () => {
        searchParams.delete('openBureauEnquiryDrawer');
        searchParams.delete('directorIdEnquiryDrawer');
        searchParams.delete('vendorTypeEnquiryDrawer');
        setSearchParams(searchParams);
        setIsBureauEnquiryDrawerOpen(false);
    };

    const fetchGraph = (graphName) => {
        const graphNameAry = [graphName];
        const directorId = searchParams.get('directorId');
        // Same reasoning as fetchCibilOverviewData above: reuse resolvedBureauType for a director
        // report so the charts stay scoped to this one report, not every bureau the director has.
        const type: string | null = directorId ? resolvedBureauType : bureauType;
        dispatch(
            fetchChartData({
                graphNames: graphNameAry,
                tabName: 'creditBureau',
                startDate: '',
                endDate: '',
                tenure: '',
                reportId: selectedCompany?.reportId || '',
                ...(directorId && { directorId: directorId }),
                type: type,
            }),
        );
    };

    const mixpanelBureauEnquiryDrawerClick = () => {
        const properties = {
            'User Role': user.userData.accountType,
            'PNO Platform': getSubdomain(),
            'User id': user.userData.userId,
            'Current URL': window.location.href,
            'Company Name': selectedCompany?.orgName,
            'Organization ID': selectedCompany?.assesseeOrgId,
            'Bureau Name': selectedDirector?.creditBureau,
        };

        Mixpanel.track('Bureau Enquiry Drawer Clicked', properties);
    };

    const getReportTitle = () => {
        switch (selectedReport) {
            case COMBINED_REPORT:
                return 'Combined Report';
            case DETAILED_REPORT:
                return selectedDirector ? selectedDirector?.name : 'Detailed Report';
            default:
                return '';
        }
    };

    const keyOfficialsCount = () => {
        const directorsCount = directorsList?.length;
        if (selectedReport === COMBINED_REPORT && directorsCount) {
            return ` (${directorsCount > 1 ? 'all ' : ''}${directorsCount} key official${directorsCount > 1 ? 's' : ''})`;
        }
        return '';
    };

    const getRiskVariantFromRiskCategory = (riskCategory) => {
        switch (riskCategory) {
            case 'Low Risk':
                return 'Low';
            case 'Moderate Risk':
                return 'Medium';
            case 'High Risk':
                return 'High';
            default:
                return 'Low';
        }
    };

    return {
        //State
        user,
        searchParams,
        chartsData,
        cibilOverviewData,
        dataType,
        isBureauEnquiryDrawerOpen,
        enquiryCount,
        totalEnquiryCount,
        bureauType,
        resolvedBureauType,
        directorId,
        selectedDirector,
        selectedReport,
        setSelectedReport,
        selectedReportConfig,
        resetQueryParams,
        isCombinedReport,
        lastConsent,

        //Methods
        setDataType,
        getImageSource,
        openBureauEnquiryDrawer,
        closeBureauEnquiryDrawer,
        fetchGraph,
        getReportTitle,
    };
};

export default useReport;
